import { useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { ConfigContext } from 'src/configContext.ts';

export function usePageChange(
    target: (page: number) => string,
    processSearchParams: (searchParams: URLSearchParams, newPage: number) => URLSearchParams,
    processLocalState?: (newPage: number) => unknown,
): [
        (newPage: number, newSearchParams: URLSearchParams) => void,
        (newPage: number, newSearchParams: URLSearchParams) => void,
    ] {
    const { rootElement } = useContext(ConfigContext);
    const navigate = useNavigate();

    const setPage = useCallback((newPage: number, newSearchParams: URLSearchParams) => {
        navigate(
            { pathname: target(newPage), search: processSearchParams(newSearchParams, newPage).toString() },
            processLocalState !== undefined ? { state: processLocalState(newPage) } : undefined,
        );
    }, [navigate, target, processSearchParams, processLocalState]);

    const handlePageChange = useCallback((p: number, searchParams: URLSearchParams) => {
        const { top = 0 } = rootElement.getBoundingClientRect();
        rootElement.ownerDocument?.scrollingElement?.scrollTo({ top });
        setPage(p, searchParams);
    }, [rootElement, setPage]);

    return [
        setPage,
        handlePageChange,
    ];
}
